<template>
  <button
    @click="emit('click')"
    :class="buttonClasses"
    :type="type"
    :disabled="disabled"
  >
    <base-loader v-if="loading" class="text-white" size="size-6 min-w-6" />
    <slot v-else>{{ label }}</slot>
  </button>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const emit = defineEmits(["click"]);

type Props = {
  label?: string;
  variant?: "primary" | "gray" | "danger" | "custom" | "transparent";
  size?: "xxs" | "xs" | "sm" | "md" | "lg";
  customClass?: string;
  type?: "button" | "submit";
  placement?: string;
  disabled?: boolean;
  loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  variant: "custom",
  size: "md",
  customClass: "",
  type: "button",
  placement: "bottom",
  disabled: false,
  loading: false,
});

const sizeClasses = {
  xxs: "size-7 min-w-7 text-xs",
  xs: "size-8 min-w-8 text-sm",
  sm: "sm:size-9 sm:min-w-9 size-9 min-w-9",
  md: "size-10 min-w-10",
  lg: "sm:size-11 sm:min-w-11 size-8 min-w-8",
};

const buttonClasses = computed(() => {
  let base =
    "cursor-pointer transition-all duration-300 flex items-center justify-center rounded-full border shrink-0";

  // Disabled state
  if (props.disabled) {
    ("bg-gray-100 text-black cursor-not-allowed");
  }

  // Size logic
  const sizeClass = sizeClasses[props.size];

  // Variant logic
  let variantClass = "";
  if (props.variant === "primary")
    variantClass =
      "bg-primary-blue/5  text-primary-blue border-primary-blue/5 hover:bg-primary-blue/10";
  else if (props.variant === "danger")
    variantClass = "bg-red-500 text-white border-red-500 hover:bg-red-600";
  else if (props.variant === "gray")
    variantClass =
      "bg-gray-100 text-black hover:bg-gray-200 border-gray-100 hover:border-gray-200";
  else if (props.variant === "transparent")
    variantClass =
      "bg-white/50 text-black hover:bg-white/80 border-transparent";
  else
    variantClass =
      props.customClass ||
      "bg-primary-blue/5 text-black border-primary/5 hover:bg-primary/10";

  return `${base} ${sizeClass} ${variantClass}`;
});
</script>
